import { GraftCodePrimitiveType } from '@graftcode/protocol'

import type { TypeUsage } from '@/core/types/library.type'

import { classifyTypeUsage, type ParameterShape } from './mappers/formatTypeUsage'

export type CoercibleInputValue = string | number | boolean

export function coerceParameterValue(
  value: CoercibleInputValue,
  typeUsageOrPrimitiveType?: TypeUsage | number
): unknown {
  const shape = resolveShape(typeUsageOrPrimitiveType)
  return coerceByShape(value, shape)
}

function resolveShape(arg: TypeUsage | number | undefined): ParameterShape {
  if (arg === undefined) return { kind: 'other' }
  if (typeof arg === 'number') return { kind: 'primitive', primitiveType: arg as GraftCodePrimitiveType }
  return classifyTypeUsage(arg)
}

function coerceByShape(value: unknown, shape: ParameterShape): unknown {
  if (shape.kind === 'array') {
    const elements = parseArrayValue(value)
    if (elements === null) {
      return value ?? ''
    }
    return elements.map((item) => coerceByShape(item, shape.element))
  }

  if (shape.kind === 'primitive') {
    return coercePrimitive(value, shape.primitiveType)
  }

  return defaultStringify(value)
}

function parseArrayValue(value: unknown): unknown[] | null {
  if (Array.isArray(value)) return value
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (trimmed.length === 0) return []

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed
    } catch {
      /* fall through */
    }
  }

  return trimmed.split(',').map((item) => stripQuotes(item.trim()))
}

function stripQuotes(s: string): string {
  if (s.length >= 2) {
    const first = s[0]
    const last = s[s.length - 1]
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return s.slice(1, -1)
    }
  }
  return s
}

function coercePrimitive(value: unknown, primitiveType: number | undefined): string | number | boolean {
  if (primitiveType === undefined) {
    return defaultStringify(value)
  }

  switch (primitiveType) {
    case GraftCodePrimitiveType.INTEGER:
    case GraftCodePrimitiveType.LONG:
    case GraftCodePrimitiveType.BYTE:
    case GraftCodePrimitiveType.UNSIGNED_INTEGER:
    case GraftCodePrimitiveType.UNSIGNED_LONG_LONG:
      return Number(value)
    case GraftCodePrimitiveType.FLOAT:
    case GraftCodePrimitiveType.DOUBLE:
      return Number(value)
    case GraftCodePrimitiveType.BOOLEAN:
      return value === true || value === 'true' || value === 1 || value === '1'
    case GraftCodePrimitiveType.STRING:
    case GraftCodePrimitiveType.CHAR:
    default:
      return typeof value === 'number' || typeof value === 'boolean' ? String(value) : String(value ?? '')
  }
}

function defaultStringify(value: unknown): string | number | boolean {
  if (typeof value === 'number' || typeof value === 'boolean') return value
  return String(value ?? '')
}
